<script setup>
import Text from "@/components/elements/Text.vue";
import { ref, defineProps, onMounted } from "vue";
import { fetchFAQs } from "../../mixins/api";
import '../../assets/scss/faqs.scss';

const props = defineProps({
    type: {
        type: String,
        required: true
    }
});

const faqs = ref([]);
const activeIndex = ref(null);

const getFAQs = () => {
    fetchFAQs(props?.type).then((response) => {
        faqs.value = response;
    });
};

const toggleFAQ = (index) => {
    if (activeIndex.value === index) {
        activeIndex.value = null; // Close if already open
    } else {
        activeIndex.value = index; // Open new FAQ
    }
};

onMounted(() => {
    getFAQs();
});
</script>
<template>
    <div class="faqs-section-wrapper" v-if="faqs.length > 0">
        <div class="faqs-accordion-container">
        <div class="faq-header">
            <!-- <Text type="H1" text="FAQs" class="faq-main-title" /> -->
            <h3>FAQs</h3>
        </div>
        
        <div class="faqs-grid">
            <!-- Left Column -->
            <div class="faqs-column">
                <div 
                    v-for="(faq, faqsIndex) in faqs.slice(0, Math.ceil(faqs.length / 2))" 
                    :key="'faq-item-left' + faqsIndex"
                    class="faq-item"
                    :class="{ 'active': activeIndex === faqsIndex }"
                >
                    <div class="faq-question" @click="toggleFAQ(faqsIndex)">
                        <div class="faq-title-with-icon">
                            <img src="https://res.cloudinary.com/ilovekickboxing/image/upload/v1/small_heart_icon_zdk1cv.png"
                                alt="Heart Icon" class="faq-heart-icon" />
                            <Text type="H3" :text="faq?.name" class="faq-question-text" />
                        </div>
                        <div class="faq-arrow" :class="{ 'expanded': activeIndex === faqsIndex }">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>
                    <div class="faq-answer" :class="{ 'expanded': activeIndex === faqsIndex }">
                        <div class="faq-answer-content">
                            <p v-html="faq?.description" class="faq-description"></p>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Right Column -->
            <div class="faqs-column">
                <div 
                    v-for="(faq, faqsIndex) in faqs.slice(Math.ceil(faqs.length / 2))" 
                    :key="'faq-item-right' + (faqsIndex + Math.ceil(faqs.length / 2))"
                    class="faq-item"
                    :class="{ 'active': activeIndex === (faqsIndex + Math.ceil(faqs.length / 2)) }"
                >
                    <div class="faq-question" @click="toggleFAQ(faqsIndex + Math.ceil(faqs.length / 2))">
                        <div class="faq-title-with-icon">
                            <img src="https://res.cloudinary.com/ilovekickboxing/image/upload/v1/small_heart_icon_zdk1cv.png"
                                alt="Heart Icon" class="faq-heart-icon" />
                            <Text type="H3" :text="faq?.name" class="faq-question-text" />
                        </div>
                        <div class="faq-arrow" :class="{ 'expanded': activeIndex === (faqsIndex + Math.ceil(faqs.length / 2)) }">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>
                    <div class="faq-answer" :class="{ 'expanded': activeIndex === (faqsIndex + Math.ceil(faqs.length / 2)) }">
                        <div class="faq-answer-content">
                            <p v-html="faq?.description" class="faq-description"></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </div>
    </div>
</template>
<style scoped>
.faqs-section-wrapper {
  width: 100%;
  background: #0a072e;
  padding: 4rem 0;
  position: relative;
  overflow-x: hidden;
}

.faqs-accordion-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  color: white;
}

.faq-header {
  text-align: center;
  margin-bottom: 3rem;
}

.faq-main-title {
  font-size: 3rem;
  font-weight: bold;
  color: white;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.faqs-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.faqs-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.faq-item {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid transparent;
}

.faq-item:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(197, 162, 84, 0.3);
}

.faq-item.active {
  background: rgba(197, 162, 84, 0.1);
  border-color: #C5A254;
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
}

.faq-question:hover {
  background: rgba(255, 255, 255, 0.05);
}

.faq-title-with-icon {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.faq-heart-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.faq-question-text {
  color: white;
  font-size: 1.1rem;
  font-weight: 400;
  margin: 0;
  line-height: 1.4;
}

.faq-arrow {
  color: #C5A254;
  transition: transform 0.3s ease;
  flex-shrink: 0;
  margin-left: 1rem;
}

.faq-arrow.expanded {
  transform: rotate(180deg);
}

.faq-arrow svg {
  display: block;
}

.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.faq-answer.expanded {
  max-height: 500px;
}

.faq-answer-content {
  padding: 0 1.5rem 1.5rem 1.5rem;
}

.faq-description {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
  padding-left: 36px; /* Align with heart icon */
}

/* Deep selector for Text component styling */
:deep(.faq-question-text) {
  color: white !important;
  font-size: 1.1rem !important;
  font-weight: 400 !important;
  margin: 0 !important;
  line-height: 1.4 !important;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .faqs-accordion-container {
    padding: 1rem;
  }
  
  .faq-main-title {
    font-size: 2rem;
  }
  
  .faqs-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .faq-question {
    padding: 1rem;
  }
  
  .faq-answer-content {
    padding: 0 1rem 1rem 1rem;
  }
  
  .faq-description {
    padding-left: 28px;
    font-size: 0.9rem;
  }
  
  .faq-heart-icon {
    width: 20px;
    height: 20px;
  }
  
  .faq-question-text {
    font-size: 1rem;
  }
}

/* Tablet */
@media (max-width: 992px) and (min-width: 769px) {
  .faqs-grid {
    gap: 1.5rem;
  }
  
  .faq-question {
    padding: 1.25rem;
  }
}
</style>